import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { useToast } from '../contexts/ToastContext';
import { CheckCircle, XCircle, RotateCcw, Trophy, Star } from 'lucide-react';

interface Question {
  id: string;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
  points: number;
  difficulty: 'easy' | 'medium' | 'hard';
}

interface BurmeseSide {
  question?: string;
  options?: string[];
  explanation?: string;
}

interface InteractiveQuizProps {
  questions: Question[];
  lessonId: string;
  title?: string;
  /** Parallel Burmese copy. Scoring still uses the English questions. */
  burmese?: BurmeseSide[];
}

const InteractiveQuiz: React.FC<InteractiveQuizProps> = ({
  questions,
  lessonId,
  title = "Test Your Knowledge",
  burmese,
}) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>(new Array(questions.length).fill(-1));
  const [showResults, setShowResults] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showBurmese, setShowBurmese] = useState(false);
  const hasBurmese = (burmese || []).some(
    (item) => item?.question || item?.explanation || item?.options?.some((option) => option.trim())
  );

  const BurmeseLine = ({ text }: { text?: string }) => {
    if (!showBurmese || !text?.trim()) return null;
    return <p className="lesson-subtext font-myanmar mt-1" lang="my">{text}</p>;
  };

  const burmeseToggle = hasBurmese ? (
    <button
      type="button"
      onClick={() => setShowBurmese((open) => !open)}
      className="rounded-full border border-orange-300 bg-white px-3 py-1 text-sm font-semibold text-orange-800"
    >
      {showBurmese ? 'Hide Burmese' : 'Show Burmese'}
    </button>
  ) : null;

  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const isFirstQuestion = currentQuestionIndex === 0;

  const handleAnswerSelect = (answerIndex: number) => {
    if (showResults) return;
    
    const newAnswers = [...selectedAnswers];
    newAnswers[currentQuestionIndex] = answerIndex;
    setSelectedAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const score = calculateScore();
      const percentage = Math.round((score / questions.length) * 100);

      // Save quiz results
      const { error } = await supabase
        .from('user_quiz_results')
        .upsert({
          user_id: user.id,
          lesson_id: lessonId,
          quiz_data: {
            questions: questions.map((q, index) => ({
              questionId: q.id,
              question: q.question,
              correctAnswer: q.correctAnswer,
              userAnswer: selectedAnswers[index],
              isCorrect: selectedAnswers[index] === q.correctAnswer
            }))
          },
          score: score,
          total_questions: questions.length,
          percentage: percentage,
          completed_at: new Date().toISOString()
        });

      if (error) throw error;

      setIsSubmitted(true);
      setShowResults(true);
      
      if (percentage >= 80) {
        showToast('success', `Excellent! You scored ${percentage}%! 🎉`);
      } else if (percentage >= 60) {
        showToast('success', `Good job! You scored ${percentage}%. Keep learning! 💪`);
      } else {
        showToast('info', `You scored ${percentage}%. Review the material and try again! 📚`);
      }
    } catch (error) {
      console.error('Error submitting quiz:', error);
      showToast('error', 'Failed to submit quiz. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const calculateScore = () => {
    return questions.reduce((score, question, index) => {
      return score + (selectedAnswers[index] === question.correctAnswer ? 1 : 0);
    }, 0);
  };

  const resetQuiz = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswers(new Array(questions.length).fill(-1));
    setShowResults(false);
    setIsSubmitted(false);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'hard': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getScoreColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (showResults) {
    const score = calculateScore();
    const percentage = Math.round((score / questions.length) * 100);

    return (
      <div className="bg-gradient-to-br from-orange-50 to-red-100 rounded-3xl p-8 border border-orange-200">
        <div className="text-center">
          <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-3xl p-6 w-24 h-24 mx-auto mb-6 flex items-center justify-center shadow-lg">
            <Trophy className="w-12 h-12 text-white" />
          </div>
          
          <h3 className="text-3xl font-bold text-gray-800 mb-4">Quiz Complete! 🎉</h3>
          
          <div className="bg-white rounded-2xl p-6 mb-6 shadow-lg">
            <div className={`text-4xl font-bold mb-2 ${getScoreColor(percentage)}`}>
              {percentage}%
            </div>
            <div className="text-lg text-gray-600 mb-4">
              {score} out of {questions.length} questions correct
            </div>
            
            <div className="flex items-center justify-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              <span className="text-sm font-medium text-gray-700">
                {percentage >= 80 ? 'Excellent!' : percentage >= 60 ? 'Good job!' : 'Keep learning!'}
              </span>
            </div>
          </div>

          <div className="space-y-4">
            {questions.map((question, index) => {
              const userAnswer = selectedAnswers[index];
              const isCorrect = userAnswer === question.correctAnswer;
              
              return (
                <div key={question.id} className="bg-white rounded-2xl p-4 border border-gray-200">
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="font-bold text-gray-800">Question {index + 1}</h4>
                    <div className={`flex items-center gap-1 ${
                      isCorrect ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {isCorrect ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <XCircle className="w-5 h-5" />
                      )}
                      <span className="text-sm font-medium">
                        {isCorrect ? 'Correct' : 'Incorrect'}
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 mb-3">{question.question}</p>
                  <BurmeseLine text={burmese?.[index]?.question} />
                  
                  <div className="space-y-2">
                    {question.options.map((option, optionIndex) => (
                      <div
                        key={optionIndex}
                        className={`p-3 rounded-xl border ${
                          optionIndex === question.correctAnswer
                            ? 'bg-green-50 border-green-200 text-green-800'
                            : optionIndex === userAnswer && !isCorrect
                            ? 'bg-red-50 border-red-200 text-red-800'
                            : 'bg-gray-50 border-gray-200 text-gray-600'
                        }`}
                      >
                        {option}
                        <BurmeseLine text={burmese?.[index]?.options?.[optionIndex]} />
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-3 p-3 bg-blue-50 rounded-xl border border-blue-200">
                    <p className="text-sm text-blue-800">
                      <strong>Explanation:</strong> {question.explanation}
                    </p>
                    <BurmeseLine text={burmese?.[index]?.explanation} />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 flex justify-center">{burmeseToggle}</div>
          <button
            onClick={resetQuiz}
            className="mt-6 flex items-center bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-3 rounded-2xl font-bold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 mx-auto"
          >
            <RotateCcw className="w-5 h-5 mr-2" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-orange-50 to-red-100 rounded-3xl p-8 border border-orange-200">
      <div className="flex items-start mb-6">
        <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-3 mr-4 flex-shrink-0 shadow-lg">
          <Trophy className="w-6 h-6 text-white" />
        </div>
        <div className="flex-grow">
          <div className="mb-2 flex flex-wrap items-center gap-3">
            <h3 className="text-2xl font-bold text-gray-800">{title} 🧠</h3>
            {burmeseToggle}
          </div>
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-600">
              Question {currentQuestionIndex + 1} of {questions.length}
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-bold ${getDifficultyColor(currentQuestion.difficulty)}`}>
              {currentQuestion.difficulty}
            </span>
            <span className="text-sm text-gray-600">
              {currentQuestion.points} points
            </span>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="w-full bg-orange-200 rounded-full h-3 overflow-hidden">
          <div
            className="bg-gradient-to-r from-orange-500 to-red-500 h-3 rounded-full transition-all duration-500 shadow-sm"
            style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="bg-white rounded-2xl p-6 mb-6 shadow-lg">
        <h4 className="text-xl font-bold text-gray-800 mb-1">
          {currentQuestion.question}
        </h4>
        <BurmeseLine text={burmese?.[currentQuestionIndex]?.question} />
        
        <div className="mt-4 space-y-3">
          {currentQuestion.options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleAnswerSelect(index)}
              className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${
                selectedAnswers[currentQuestionIndex] === index
                  ? 'bg-purple-50 border-purple-300 text-purple-800'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-purple-200 hover:bg-purple-25'
              }`}
            >
              <div className="flex items-start">
                <div className={`mt-0.5 w-6 h-6 rounded-full border-2 mr-3 flex items-center justify-center flex-shrink-0 ${
                  selectedAnswers[currentQuestionIndex] === index
                    ? 'border-purple-500 bg-purple-500'
                    : 'border-gray-300'
                }`}>
                  {selectedAnswers[currentQuestionIndex] === index && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
                <span className="font-medium">
                  {option}
                  <BurmeseLine text={burmese?.[currentQuestionIndex]?.options?.[index]} />
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePrevious}
          disabled={isFirstQuestion}
          className={`flex items-center px-6 py-3 rounded-2xl font-medium transition-all duration-200 ${
            isFirstQuestion
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Previous
        </button>

        <div className="flex items-center gap-3">
          {isLastQuestion ? (
            <button
              onClick={handleSubmit}
              disabled={selectedAnswers[currentQuestionIndex] === -1 || isLoading}
              className="flex items-center bg-gradient-to-r from-green-500 to-emerald-500 text-white px-8 py-3 rounded-2xl font-bold hover:from-green-600 hover:to-emerald-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              <Trophy className="w-5 h-5 mr-2" />
              {isLoading ? 'Submitting...' : 'Submit Quiz'}
            </button>
          ) : (
            <button
              onClick={handleNext}
              disabled={selectedAnswers[currentQuestionIndex] === -1}
              className="flex items-center bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-2xl font-bold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InteractiveQuiz;